import { useState } from "react";
import { motion } from "framer-motion";
import { useMatch } from "@/match/MatchContext";

// Shared editorial story moment — used by Captain + Reality Check.
// 16:10 media slot on one side, minute/headline/preview + commentary on the other.
export default function StorySection({ section, flip = false, bg = "#F2F2F0" }) {
  const { openCommentary } = useMatch();
  const [mediaSrc, setMediaSrc] = useState(section.mediaUrl || null);

  return (
    <div
      className="py-16 sm:py-24 px-4 sm:px-8 lg:px-16 border-b-4 border-black"
      style={{ backgroundColor: bg }}
    >
      <div
        className={`max-w-[1500px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center ${
          flip ? "lg:[&>*:first-child]:order-2" : ""
        }`}
      >
        {/* media slot */}
        <motion.div
          className="relative aspect-[16/10] border-4 border-black hard-shadow-lg bg-[#0A0A0A] overflow-hidden"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
          data-testid={`${section.id}-media`}
        >
          {mediaSrc ? (
            <img
              src={mediaSrc}
              alt={section.mediaAlt || section.headline}
              className="w-full h-full object-cover"
              style={{ objectPosition: section.mediaPosition || "center" }}
              onError={() => setMediaSrc(null)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <svg viewBox="0 0 64 64" className="w-14 h-14 opacity-60" aria-hidden="true">
                <rect x="4" y="10" width="56" height="44" rx="3" fill="none" stroke="#84E116" strokeWidth="3" />
                <circle cx="32" cy="32" r="12" fill="none" stroke="#84E116" strokeWidth="3" />
                <path d="M32 26 L37 30 L35 37 L29 37 L27 30 Z" fill="#84E116" />
              </svg>
            </div>
          )}
        </motion.div>

        {/* text side */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.65, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
        >
          <span
            className="inline-block bg-[#0A0A0A] text-[#84E116] font-broadcast font-bold text-[10px] sm:text-xs tracking-[0.28em] uppercase px-4 py-2 mb-6"
            data-testid={`${section.id}-minute`}
          >
            {section.minuteEvent}
          </span>
          <h2 className="font-display uppercase text-3xl sm:text-4xl lg:text-5xl leading-[0.95] tracking-tight text-black mb-6">
            {section.headline}
          </h2>
          <p className="text-base sm:text-lg font-medium leading-relaxed text-black/75 max-w-xl mb-8">
            {section.preview}
          </p>
          <CommentaryButton onClick={() => openCommentary(section.id)} testId={`${section.id}-commentary-btn`} />
        </motion.div>
      </div>
    </div>
  );
}

export function CommentaryButton({ onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="group inline-flex items-center gap-2.5 bg-white border-[3px] border-black hard-shadow px-6 py-3 font-broadcast font-extrabold text-xs sm:text-sm tracking-[0.2em] uppercase text-black transition-all duration-200 hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[6px_6px_0_0_#000] active:translate-x-0.5 active:translate-y-0.5 active:shadow-none"
    >
      <span className="w-2 h-2 rounded-full bg-[#FF3B30] live-dot" />
      Commentary
      <span className="inline-block transition-transform duration-200 group-hover:translate-x-1">→</span>
    </button>
  );
}
